import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useHomeLogic = () => {
  const [offers, setOffers] = useState([]);
  const [expiringSoon, setExpiringSoon] = useState([]);
  const [categories, setCategories] = useState(["All"]);
  const [selectedCategory, setSelectedCategory] = useState([]);
  const [sortBy, setSortBy] = useState("urgency");
  const [searchQuery, setSearchQuery] = useState("");

  // Dietary filters (work in both modes)
  const [halalFilter, setHalalFilter] = useState(false);
  const [halalCertifiedFilter, setHalalCertifiedFilter] = useState(false);
  const [veganFilter, setVeganFilter] = useState(false);
  const [vegetarianFilter, setVegetarianFilter] = useState(false);
  const [glutenFreeFilter, setGlutenFreeFilter] = useState(false);

  // Offer filters (Birthday mode only)
  const [boycottFilter, setBoycottFilter] = useState(false);
  const [noAppFilter, setNoAppFilter] = useState(false);
  const [expiringSoonFilter, setExpiringSoonFilter] = useState(false);
  const [highValueFilter, setHighValueFilter] = useState(false);
  const [easyClaimFilter, setEasyClaimFilter] = useState(false);
  const [verifiedFilter, setVerifiedFilter] = useState(false);
  const [trueFreebieFilter, setTrueFreebieFilter] = useState(false);
  const [bogoFilter, setBogoFilter] = useState(false);
  const [rewardTypeFilter, setRewardTypeFilter] = useState("");
  const [inStoreFilter, setInStoreFilter] = useState(false);
  const [kidsFilter, setKidsFilter] = useState(false);
  const [windowFilter, setWindowFilter] = useState("");

  const [stats, setStats] = useState({ days_until_birthday: 30, total_offers: 0 });
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("birthday");
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [bestValue, setBestValue] = useState([]);

  // Saved dietary defaults (from user profile)
  const [savedDietaryDefaults, setSavedDietaryDefaults] = useState({ halal: false, vegan: false, vegetarian: false, gluten_free: false });
  const defaultsLoaded = useRef(false);

  const hasDietaryFilter = halalFilter || halalCertifiedFilter || veganFilter || vegetarianFilter || glutenFreeFilter;

  // Load dietary preferences from user profile on mount
  useEffect(() => {
    if (defaultsLoaded.current) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/dietary-preferences`);
        const prefs = res.data || {};
        setSavedDietaryDefaults(prefs);
        // Apply saved defaults as initial filter state
        if (prefs.halal) setHalalFilter(true);
        if (prefs.vegan) setVeganFilter(true);
        if (prefs.vegetarian) setVegetarianFilter(true);
        if (prefs.gluten_free) setGlutenFreeFilter(true);
        defaultsLoaded.current = true;
      } catch (e) {
        defaultsLoaded.current = true;
      }
    })();
  }, []);

  // Save current dietary filter state as new defaults
  const saveDietaryDefaults = useCallback(async () => {
    const prefs = {
      halal: halalFilter || halalCertifiedFilter,
      vegan: veganFilter,
      vegetarian: vegetarianFilter,
      gluten_free: glutenFreeFilter,
    };
    try {
      const res = await axios.put(`${API}/auth/dietary-preferences`, prefs);
      setSavedDietaryDefaults(res.data.dietary_preferences);
      return true;
    } catch (e) {
      console.error("Failed to save dietary defaults:", e);
      return false;
    }
  }, [halalFilter, halalCertifiedFilter, veganFilter, vegetarianFilter, glutenFreeFilter]);

  // Check if current dietary filters differ from saved defaults
  const dietaryDiffersFromDefaults = (halalFilter || halalCertifiedFilter) !== savedDietaryDefaults.halal
    || veganFilter !== savedDietaryDefaults.vegan
    || vegetarianFilter !== savedDietaryDefaults.vegetarian
    || glutenFreeFilter !== savedDietaryDefaults.gluten_free;

  const fetchFavorites = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/favorites`);
      setFavoriteIds(new Set(res.data.map(f => f.business_id)));
    } catch (e) { console.error(e); }
  }, []);

  const toggleFavorite = async (businessId) => {
    try {
      const res = await axios.post(`${API}/favorites/${businessId}`);
      setFavoriteIds(prev => {
        const next = new Set(prev);
        res.data.favorited ? next.add(businessId) : next.delete(businessId);
        return next;
      });
    } catch (e) { console.error(e); }
  };

  const fetchData = useCallback(async () => {
    const isDiscover = mode === "discover";

    // Discover mode requires at least one dietary filter
    if (isDiscover && !hasDietaryFilter) {
      setOffers([]);
      setLoading(false);
      return;
    }

    try {
      const params = { mode, sort: isDiscover ? "name" : sortBy, limit: isDiscover ? 100 : 50 };

      // Dietary filters (both modes)
      if (halalCertifiedFilter) params.halal_certified = "yes";
      else if (halalFilter) params.halal = "yes";
      if (veganFilter) params.vegan = "yes";
      if (vegetarianFilter) params.vegetarian = "yes";
      if (glutenFreeFilter) params.gluten_free = "yes";
      if (boycottFilter) params.boycott_filter = "exclude";

      // Offer filters (Birthday mode only — backend will ignore in discover)
      if (!isDiscover) {
        if (selectedCategory.length > 0) params.category = selectedCategory.join(",");
        if (searchQuery) params.search = searchQuery;
        if (noAppFilter) params.no_app = "yes";
        if (expiringSoonFilter) params.expiring_soon = "yes";
        if (highValueFilter) params.high_value = "yes";
        if (easyClaimFilter) params.easy_claim = "yes";
        if (verifiedFilter) params.verified = "yes";
        if (trueFreebieFilter) params.true_freebie = "yes";
        if (bogoFilter) params.bogo = "yes";
        if (rewardTypeFilter) params.reward_type = rewardTypeFilter;
        if (inStoreFilter) params.in_store_only = "yes";
        if (kidsFilter) params.kids_friendly = "yes";
        if (windowFilter) params.window_days = windowFilter;
      }

      const requests = [
        axios.get(`${API}/offers`, { params }),
        axios.get(`${API}/categories`),
        axios.get(`${API}/stats`),
      ];

      if (!isDiscover) {
        requests.push(axios.get(`${API}/offers/expiring-soon`));
        const bestValueParams = {};
        if (selectedCategory.length > 0) bestValueParams.category = selectedCategory.join(",");
        requests.push(axios.get(`${API}/offers/best-value`, { params: bestValueParams }));
      }

      const results = await Promise.all(requests);
      setOffers(results[0].data);
      setCategories(results[1].data.categories);
      setStats(results[2].data);
      if (!isDiscover) {
        setExpiringSoon(results[3]?.data || []);
        setBestValue(results[4]?.data || []);
      }
    } catch (e) {
      console.error("Error fetching data:", e);
    } finally {
      setLoading(false);
    }
  }, [mode, selectedCategory, sortBy, searchQuery, halalFilter, halalCertifiedFilter,
      veganFilter, vegetarianFilter, glutenFreeFilter, boycottFilter, noAppFilter, expiringSoonFilter,
      highValueFilter, easyClaimFilter, verifiedFilter, trueFreebieFilter, bogoFilter,
      rewardTypeFilter, inStoreFilter, kidsFilter, windowFilter, hasDietaryFilter]);

  useEffect(() => { fetchData(); fetchFavorites(); }, [fetchData, fetchFavorites]);

  return {
    offers, expiringSoon, categories, selectedCategory, setSelectedCategory,
    sortBy, setSortBy, searchQuery, setSearchQuery,
    halalFilter, setHalalFilter, halalCertifiedFilter, setHalalCertifiedFilter,
    veganFilter, setVeganFilter, vegetarianFilter, setVegetarianFilter,
    glutenFreeFilter, setGlutenFreeFilter,
    boycottFilter, setBoycottFilter,
    noAppFilter, setNoAppFilter, expiringSoonFilter, setExpiringSoonFilter,
    highValueFilter, setHighValueFilter, easyClaimFilter, setEasyClaimFilter,
    verifiedFilter, setVerifiedFilter, trueFreebieFilter, setTrueFreebieFilter,
    bogoFilter, setBogoFilter, rewardTypeFilter, setRewardTypeFilter,
    inStoreFilter, setInStoreFilter, kidsFilter, setKidsFilter,
    windowFilter, setWindowFilter,
    stats, loading, mode, setMode, favoriteIds, toggleFavorite, bestValue,
    hasDietaryFilter, saveDietaryDefaults, dietaryDiffersFromDefaults,
  };
};
